import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { centralApi } from '@marque/api/central'

// AB#2457 — the platform Google keys tenants may opt into (per-tenant tick boxes on their
// Integrations page). The Maps CLIENT key is browser-delivered by nature (referrer-restricted —
// restrict it to every tenant domain in the Google console); the Places SERVER key is a true
// secret: write-only here, stored via Key Vault, never shown back.
const field = 'w-full rounded-md border border-neutral-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

export function GoogleKeysCard() {
    const qc = useQueryClient()
    const [mapsEdited, setMapsEdited] = useState<string | null>(null)
    const [placesEdited, setPlacesEdited] = useState<string | null>(null)

    const { data, isLoading } = useQuery({
        queryKey: ['google-config'],
        queryFn: centralApi.getGoogleConfig,
    })

    const save = useMutation({
        mutationFn: () => centralApi.setGoogleConfig({
            ...(mapsEdited !== null ? { mapsClientKey: mapsEdited.trim() } : {}),
            ...(placesEdited !== null ? { placesServerKey: placesEdited.trim() } : {}),
        }),
        onSuccess: () => {
            setMapsEdited(null)
            setPlacesEdited(null)
            void qc.invalidateQueries({ queryKey: ['google-config'] })
        },
    })

    const dirty = mapsEdited !== null || placesEdited !== null

    return (
        <div className="bg-white rounded-lg border border-neutral-200 p-6 mt-6">
            <h2 className="text-lg font-semibold text-neutral-900">Maps &amp; Addresses — Platform Google Keys</h2>
            <p className="mt-1 text-sm text-neutral-500">
                Tenants can opt into these instead of providing their own keys (Integrations page tick boxes).
                The Maps key loads maps in tenant browsers — restrict it by HTTP referrer, not secrecy.
                The Places key never leaves the server and is stored as a Key Vault secret.
            </p>
            <div className="mt-4">
                <label className="block text-sm font-medium text-neutral-700 mb-1">Maps client key</label>
                <input
                    type="text"
                    value={mapsEdited ?? data?.mapsClientKey ?? ''}
                    onChange={e => setMapsEdited(e.target.value)}
                    placeholder="AIza… (browser key, referrer-restricted)"
                    autoComplete="off"
                    className={field}
                    disabled={isLoading}
                />
            </div>
            <div className="mt-4">
                <label className="block text-sm font-medium text-neutral-700 mb-1">Places server key</label>
                <input
                    type="password"
                    value={placesEdited ?? ''}
                    onChange={e => setPlacesEdited(e.target.value)}
                    placeholder={data?.hasPlacesServerKey ? 'Enter a new key to replace, save empty to clear' : 'AIza… (server key, API-restricted)'}
                    autoComplete="off"
                    className={field}
                    disabled={isLoading}
                />
                {data?.hasPlacesServerKey && placesEdited === null && (
                    <p className="mt-1 text-xs text-emerald-600">A places server key is configured — the value is never shown back.</p>
                )}
            </div>
            <div className="mt-4 flex items-center gap-3">
                <button
                    type="button"
                    disabled={!dirty || save.isPending}
                    onClick={() => save.mutate()}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                >
                    {save.isPending ? 'Saving…' : 'Save keys'}
                </button>
                {save.isSuccess && !dirty && <span className="text-sm text-emerald-600">Saved.</span>}
                {save.isError && <span className="text-sm text-red-600">Failed to save.</span>}
            </div>
        </div>
    )
}
